/**
 * Base Swiper Class
 * Optimization: Used DOMContentLoaded instead of 'load' event.
 */
class InstanceSwiper extends HTMLElement {
  constructor() {
    super();
    this.handle = this.getAttribute('handle');
    this.swiperInitialized = false; // Prevent multiple initializations
  }

  static get observedAttributes() {
    return ['handle'];
  }

  init() {
    // Stop if already initialized or element is not visible
    if (this.swiperInitialized) return;

    // Skip initialization if element is not visible (has no dimensions)
    if (this.offsetWidth === 0 && this.offsetHeight === 0 && !this.checkVisibility()) return;

    this.swiperInitialized = true;

    this.options = this.setOptions();
    // Scoped so several galleries with the same handle can coexist on one page.
    this.instance = new Swiper(this.querySelector('.swiper--' + this.handle) || '.swiper--' + this.handle, this.options);
    this.setInteractions();
  }

  /**
   * Checks if the element is visible
   */
  checkVisibility() {
    return !!(this.offsetWidth || this.offsetHeight || this.getClientRects().length);
  }

  connectedCallback() {
    if (Shopify.designMode) {
      window.addEventListener('shopify:section:load', () => this.init());
      window.addEventListener('shopify:section:select', () => this.init());

      // Fallback for Design mode
      setTimeout(() => {
        if (!this.swiperInitialized) this.init();
      }, 100);
    } else {
      // OPTIMIZATION: Not waiting for window 'load'. Starts immediately if DOM is ready.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }
  }

  disconnectedCallback() {
    if (this.instance && this.instance.destroy) this.instance.destroy();
  }

  /**
   * Settings before initialization
   */
  setOptions() {
    this._options = this.getOptionsAsJsonScripts();
    return this._options;
  }

  getOptionsAsJsonScripts() {
    const jsonScriptAutomated = this.querySelector(`script#swiper--${this.handle}--automated-options`);
    const jsonScriptOverwrite = this.querySelector(`script#swiper--${this.handle}--overwrite-options`);
    let options = {};

    const parseJSON = scriptElement => {
      if (scriptElement && scriptElement.textContent) {
        try {
          return JSON.parse(scriptElement.textContent);
        } catch (error) {
          console.error('JSON Parse Error', error, scriptElement?.textContent);
          return {};
        }
      }
      return {};
    };

    options = { ...options, ...parseJSON(jsonScriptAutomated) };
    options = { ...options, ...parseJSON(jsonScriptOverwrite) };

    return options;
  }

  /** Thumbs instance should be mounted before this instance */
  getThumbsInstance() {
    if (!this.isThumbsActive) return;
    const scope = this.closest('.main-product__media') || document;
    return scope.querySelector(`[handle="${this.getAttribute('thumbs')}"]`)?.instance;
  }

  /**
   * Interactions after initialization
   */
  setInteractions() {
    // console.log('setInteractions');
  }
}

/**
 * Product Thumbnails Class
 * Optimization: Used ResizeObserver instead of setTimeout(2500).
 */
class InstanceSwiperProductThumbs extends InstanceSwiper {
  constructor() {
    super();
  }

  setOptions() {
    super.setOptions();
    this.setCenteredSlides();
    return this._options;
  }

  setCenteredSlides() {
    if (this.handle !== 'product-thumbs') return;

    const afterInit = swiper => {
      swiper.slideTo(swiper.activeIndex);
      const swiperWrapper = swiper.wrapperEl;

      const observer = new MutationObserver(resetSwiperWrapperTransform);
      observer.observe(swiperWrapper, { attributes: true });

      function resetSwiperWrapperTransform() {
        if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform !== 'translate3d(0px, 0px, 0px)'
        ) {
          swiperWrapper.style.transform = 'translate3d(0px, 0px, 0px)';
        } else if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
        ) {
          setTimeout(() => {
            if (
              swiperWrapper.hasAttribute('style') &&
              swiperWrapper.style?.transform &&
              swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
            ) {
              observer.disconnect();
            }
          }, 500);
        }
      }

      /**
       * OPTIMIZATION: Quick fix for thumbnail dislocation issue.
       * Used ResizeObserver instead of fixed 2500ms wait.
       */
      const slideElement = this.querySelector('.swiper-slide');

      const calculateAndSetHeight = () => {
        if (!slideElement) return;
        const slideHeight = slideElement.clientHeight;
        // Do not calculate if not rendered (is 0)
        if (!slideHeight) return;

        const calculateAllThumbHeight = (slideHeight + 16) * this.querySelectorAll('.swiper-slide').length;
        const thumbsWrapper = this.parentElement;

        if (thumbsWrapper.offsetHeight === calculateAllThumbHeight || calculateAllThumbHeight <= thumbsWrapper.offsetHeight) return;

        const calculatedHeight = calculateAllThumbHeight - 16 + 'px';

        if (thumbsWrapper.offsetHeight > calculateAllThumbHeight) {
          thumbsWrapper.style.height = calculatedHeight;
        } else {
          thumbsWrapper.removeAttribute('style');
        }
      };

      // Initial run
      calculateAndSetHeight();

      // Listen for changes (CSS loading, screen size change, etc.)
      const resizeObserver = new ResizeObserver(() => {
        calculateAndSetHeight();
      });

      // Observe parent element size changes
      if (this.parentElement) {
        resizeObserver.observe(this.parentElement);
      }
    };

    this._options = {
      ...this._options,
      on: {
        ...(this._options.on || {}),
        afterInit: afterInit
      }
    };
  }
}

if (!customElements.get('swiper-product-thumbs')) {
  customElements.define('swiper-product-thumbs', InstanceSwiperProductThumbs);
}

/**
 * Main Product Gallery Class
 */
class InstanceSwiperProductGallery extends InstanceSwiper {
  constructor() {
    super();
    this.isThumbsActive = this.hasAttribute('thumbs');
    this.isZoomActive = this.hasAttribute('zoom');
    this.isCustomLayout = false;
    this.modelViewerBtn = this.querySelector('.model-viewer-btn');
    this.modelViewer = this.querySelector('model-viewer');
    this.isModelActive = false;
    this._isAdjustingCustom = false;
    this._customInteractionBound = false;
    this._customBoundResizeHandler = null;
    this._customInitTimer = null;
    this._thumbsInteractionBound = false;
    this._thumbsInteractionAttempts = 0;
  }

  connectedCallback() {
    super.connectedCallback();

    // Fallback: ensure custom gallery behavior is applied after Swiper is ready.
    let attempts = 0;
    const tryInitCustom = () => {
      attempts += 1;
      this.isCustomLayout = !!this.closest('.product--custom-layout');
      if (this.instance && this.isCustomLayout && !this._customInteractionBound) {
        this.setCustomLayoutInteraction();
        return;
      }
      if (attempts < 25 && !this._customInteractionBound) {
        this._customInitTimer = setTimeout(tryInitCustom, 80);
      }
    };
    this._customInitTimer = setTimeout(tryInitCustom, 40);
  }

  init() {
    super.init();

    this.isCustomLayout = !!this.closest('.product--custom-layout');
    if (this.isCustomLayout && this.instance && !this._customInteractionBound) {
      this.setCustomLayoutInteraction();
    }
  }

  static get observedAttributes() {
    return ['thumbs', 'zoom'];
  }

  setOptions() {
    super.setOptions();
    this.setThumbOptions();
    return this._options;
  }

  setThumbOptions() {
    if (!this.isThumbsActive) return;
    const thumbsInstance = super.getThumbsInstance();
    this._options = {
      ...this._options,
      thumbs: {
        swiper: thumbsInstance
      }
    };
  }

  /**
   * Interactions after initialization
   */
  setInteractions() {
    this.isCustomLayout = !!this.closest('.product--custom-layout');

    if (this.isThumbsActive) this.setThumbsInteraction();
    if (this.isZoomActive) this.photoSwipeLightboxInit();
    this.initModelViewer();
    if (this.isCustomLayout) this.setCustomLayoutInteraction();

    // Add thumb click handler for model slides
    const thumbSlides = document.querySelectorAll('.swiper-slide[data-media-type="model"]');
    thumbSlides.forEach(slide => {
      slide.addEventListener('click', () => this.enableSwiper());
    });

    // Force pagination update on mobile after each slide change
    this.instance.on('slideChange', swiper => {
      if (DeviceDetector.isMobile()) {
        swiper.pagination.update();
      }
    });

    // Detect scroll event and force update pagination when scrolling ends
    this.instance.on('scroll', swiper => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        swiper.pagination.update();
      }, 200);
    });

    this.initGalleryPreviews();
  }

  initModelViewer() {
    // Find all swiper slides with model viewers
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    this.resetAllModelViewers();
    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const container = slide.querySelector('.model-viewer-container');
      const modelViewer = container?.querySelector('model-viewer');

      if (btn && container && modelViewer) {
        // Store references to associated elements
        btn.modelViewer = modelViewer;
        modelViewer.controlButton = btn;

        // Model viewer container click handler
        container.addEventListener('click', e => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
            btn.click();
          }
        });

        // Model viewer button click handler
        btn.addEventListener('click', e => {
          e.preventDefault();
          this.toggleModelViewer(btn, modelViewer);
        });

        // Prevent model-viewer direct interaction
        modelViewer.addEventListener('mousedown', e => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
          }
        });
      }
    });

    // Add navigation click handlers
    if (this.instance?.navigation) {
      const { nextEl, prevEl } = this.instance.navigation;
      if (nextEl) {
        nextEl.addEventListener('click', () => {
          this.resetAllModelViewers();
          this.enableSwiper(true);
        });
      }
      if (prevEl) {
        prevEl.addEventListener('click', () => {
          this.resetAllModelViewers();
          this.enableSwiper(true);
        });
      }
    }
  }

  resetAllModelViewers() {
    // Find all model viewers in the current gallery instance
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const modelViewer = slide.querySelector('model-viewer');

      if (btn && modelViewer) {
        btn.classList.remove('is-active');
        modelViewer.interactionPrompt = 'none';
        modelViewer.cameraControls = false;
      }
    });

    this.isModelActive = false;
    this.instance && (this.instance.allowTouchMove = true);
  }

  toggleModelViewer(btn, modelViewer) {
    const wasActive = btn.classList.contains('is-active');

    // Reset all model viewers in this gallery
    this.resetAllModelViewers();

    if (!wasActive) {
      this.isModelActive = true;
      this.instance.allowTouchMove = false;

      // Enable only this specific model viewer
      modelViewer.interactionPrompt = 'auto';
      modelViewer.cameraControls = true;
      btn.classList.add('is-active');
    }
  }

  temporarilyDisableSwiper() {
    // Only disable touch/mouse dragging
    this.instance.allowTouchMove = false;

    // Re-enable after a short delay if no model interaction occurs
    this.swiperTimeout = setTimeout(() => {
      if (!this.isModelActive) {
        this.enableSwiper();
      }
    }, 100);
  }

  enableSwiper(force = false) {
    if (this.swiperTimeout) {
      clearTimeout(this.swiperTimeout);
    }

    // Always enable touch/mouse dragging if force is true or model is not active
    if (force || !this.isModelActive) {
      this.instance.allowTouchMove = true;

      // Reset model viewer state if forced
      if (force && this.isModelActive) {
        this.isModelActive = false;
        if (this.modelViewer) {
          this.modelViewer.interactionPrompt = 'none';
          this.modelViewer.cameraControls = false;
        }
        this.modelViewerBtn.classList.remove('is-active');
      }
    }
  }

  setThumbsInteraction() {
    if (!this.isThumbsActive || !this.instance) return;
    if (this._thumbsInteractionBound) return;

    const thumbsSwiper = this.options?.thumbs?.swiper || super.getThumbsInstance?.();
    if (!thumbsSwiper?.el) {
      if (this._thumbsInteractionAttempts < 20) {
        this._thumbsInteractionAttempts += 1;
        setTimeout(() => this.setThumbsInteraction(), 80);
      }
      return;
    }

    this._thumbsInteractionBound = true;
    this._thumbsInteractionAttempts = 0;

    // Enable swiper on thumb click
    thumbsSwiper.el.addEventListener('click', () => {
      this.enableSwiper(true);
      if (this.modelViewerBtn) {
        this.modelViewerBtn.classList.remove('is-active');
      }
    });

    this.instance.on('slideChangeTransitionStart', function (swiper) {
      const activeIndex = swiper.activeIndex;
      const thumbsActiveIndex = thumbsSwiper.activeIndex;
      if (activeIndex !== thumbsActiveIndex) {
        thumbsSwiper.slideTo(activeIndex);
      }
    });
  }

  setCustomLayoutInteraction() {
    if (!this.instance || this._customInteractionBound) return;
    this._customInteractionBound = true;

    const thumbsSwiper = this.isThumbsActive ? this.options?.thumbs?.swiper : null;
    const navNext = this.instance.navigation?.nextEl;
    const navPrev = this.instance.navigation?.prevEl;
    const isDesktopViewport = () => window.matchMedia('(min-width: 1024px)').matches;

    const ensureMobileVerticalDots = () => {
      if (isDesktopViewport()) return;

      let paginationEl = this.instance.el.parentElement?.querySelector('.swiper--product-gallery__pagination--custom');
      if (!paginationEl) {
        paginationEl = document.createElement('div');
        paginationEl.className = 'swiper--product-gallery__pagination swiper--product-gallery__pagination--custom swiper-pagination';
        this.instance.el.parentElement?.appendChild(paginationEl);
      }

      this.instance.params.pagination = {
        ...(this.instance.params.pagination || {}),
        el: paginationEl,
        clickable: true,
      };

      if (this.instance.pagination && typeof this.instance.pagination.destroy === 'function') {
        this.instance.pagination.destroy();
      }
      if (this.instance.pagination && typeof this.instance.pagination.init === 'function') {
        this.instance.pagination.init();
        this.instance.pagination.render();
        this.instance.pagination.update();
      }
    };

    const setGalleryDirection = direction => {
      if (typeof this.instance.changeDirection === 'function') {
        if (this.instance.params.direction !== direction) {
          this.instance.changeDirection(direction);
        }
      } else {
        this.instance.params.direction = direction;
      }
    };

    if (!isDesktopViewport()) {
      const slider = this.instance.el;
      slider.classList.remove('is-hero-state');
      slider.classList.remove('is-split-state');
      slider.classList.remove('is-transitioning-to-split');

      setGalleryDirection('vertical');
      this.instance.params.slidesPerView = 1;
      this.instance.params.slidesPerGroup = 1;
      this.instance.params.spaceBetween = 0;
      this.instance.allowSlideNext = true;
      this.instance.allowSlidePrev = true;
      this.instance.update();
      ensureMobileVerticalDots();
      this.updateCustomThumbsState();

      this._customBoundResizeHandler = () => {
        if (!isDesktopViewport()) return;
        window.removeEventListener('resize', this._customBoundResizeHandler);
        this._customInteractionBound = false;
        this.setCustomLayoutInteraction();
      };
      window.addEventListener('resize', this._customBoundResizeHandler);
      return;
    }

    const applyDesktopState = targetIndex => {
      if (this._isAdjustingCustom) return;
      this._isAdjustingCustom = true;

      const slider = this.instance.el;
      const totalSlides = this.instance.slides.length;

      if (targetIndex <= 0 || totalSlides <= 1) {
        // Suprimir transición de width/height durante la vuelta al slide 0
        // para evitar el salto visual al pasar de aspect-ratio 1/1 a 2/1
        slider.classList.add('swiper-no-transition');
        slider.classList.add('is-hero-state');
        slider.classList.remove('is-split-state');
        slider.classList.remove('is-transitioning-to-split');
        setGalleryDirection('horizontal');
        this.instance.params.slidesPerView = 1;
        this.instance.params.slidesPerGroup = 1;
        this.instance.params.spaceBetween = 0;
        this.instance.allowSlideNext = true;
        this.instance.allowSlidePrev = true;
        this.instance.update();
        if (this.instance.activeIndex !== 0) {
          this.instance.slideTo(0, 380, false);
        }
        // Restaurar transiciones en el siguiente frame para que Swiper haya recalculado
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            slider.classList.remove('swiper-no-transition');
          });
        });
      } else {
        const wasHero = slider.classList.contains('is-hero-state');
        slider.classList.remove('is-hero-state');
        slider.classList.add('is-split-state');

        if (wasHero) {
          slider.classList.add('is-transitioning-to-split');
          if (this._splitTransitionTimer) clearTimeout(this._splitTransitionTimer);
          this._splitTransitionTimer = setTimeout(() => {
            slider.classList.remove('is-transitioning-to-split');
          }, 420);
        }

        setGalleryDirection('horizontal');
        this.instance.params.slidesPerView = 2;
        this.instance.params.slidesPerGroup = 1;
        this.instance.params.spaceBetween = 0;
        this.instance.allowSlideNext = true;
        this.instance.allowSlidePrev = true;
        this.instance.update();

        const leftIndex = Math.max(Math.min(targetIndex - 1, totalSlides - 2), 0);
        if (this.instance.activeIndex !== leftIndex) {
          this.instance.slideTo(leftIndex, 380, false);
        }
      }

      this.updateCustomThumbsState();
      this._isAdjustingCustom = false;
    };

    const applyMobileState = () => {
      if (this._isAdjustingCustom) return;
      this._isAdjustingCustom = true;

      const slider = this.instance.el;
      slider.classList.remove('is-hero-state');
      slider.classList.remove('is-split-state');
      slider.classList.remove('is-transitioning-to-split');

      setGalleryDirection('vertical');
      this.instance.params.slidesPerView = 1;
      this.instance.params.slidesPerGroup = 1;
      this.instance.params.spaceBetween = 0;
      this.instance.allowSlideNext = true;
      this.instance.allowSlidePrev = true;
      this.instance.update();
      ensureMobileVerticalDots();
      this.updateCustomThumbsState();
      this._isAdjustingCustom = false;
    };

    const applyStateByViewport = (preferredIndex = this.instance.activeIndex) => {
      if (isDesktopViewport()) {
        applyDesktopState(preferredIndex);
      } else {
        applyMobileState();
      }
    };

    this._applyDesktopState = applyDesktopState;

    applyStateByViewport(0);

    if (navNext) {
      navNext.addEventListener('click', e => {
        if (!isDesktopViewport()) return;
        if (!this.instance.el.classList.contains('is-hero-state')) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        applyDesktopState(Math.min(this.instance.activeIndex + 1, this.instance.slides.length - 1));
      }, true);
    }

    if (navPrev) {
      navPrev.addEventListener('click', e => {
        if (!isDesktopViewport()) return;
        if (!this.instance.el.classList.contains('is-split-state')) return;
        if (this.instance.activeIndex !== 0) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        applyDesktopState(0);
      }, true);
    }

    // In split-state at index 0, Swiper disables prevEl (pointer-events: none)
    // because it thinks we're at the beginning. We need it enabled so the user
    // can click prev to return to hero-state.
    const enforceSplitPrevEnabled = () => {
      if (!isDesktopViewport()) return;
      if (!navPrev) return;
      if (this.instance.el.classList.contains('is-split-state') && this.instance.isBeginning) {
        navPrev.classList.remove('swiper-button-disabled');
      }
    };

    this.instance.on('reachBeginning', enforceSplitPrevEnabled);
    this.instance.on('update', enforceSplitPrevEnabled);
    this.instance.on('transitionEnd', enforceSplitPrevEnabled);

    this.instance.on('slideChange', swiper => {
      if (isDesktopViewport()) {
        const isHero = swiper.el.classList.contains('is-hero-state');
        const isSplit = swiper.el.classList.contains('is-split-state');

        if (isHero && swiper.activeIndex > 0) {
          applyDesktopState(swiper.activeIndex);
          return;
        }

        if (isSplit) {
          this.updateCustomThumbsState();
          return;
        }

        this.updateCustomThumbsState();
      } else {
        this.updateCustomThumbsState();
      }
    });

    if (thumbsSwiper?.el) {
      thumbsSwiper.el.addEventListener('click', e => {
        const clickedSlide = e.target.closest('.swiper-slide[data-index]');
        if (!clickedSlide) return;
        const clickedIndex = Number(clickedSlide.dataset.index || 0);
        applyStateByViewport(clickedIndex);
      });
    }

    this._customBoundResizeHandler = () => {
      applyStateByViewport(this.instance.activeIndex);
    };
    window.addEventListener('resize', this._customBoundResizeHandler);
  }

  updateCustomThumbsState() {
    if (!this.isCustomLayout || !this.isThumbsActive) return;
    const thumbSlides = Array.from(
      this.closest('.main-product__media')?.querySelectorAll('.swiper--product-thumbs .swiper-slide[data-index]') || []
    );
    if (!thumbSlides.length) return;

    thumbSlides.forEach(slide => {
      slide.classList.remove('is-visible-pair');
    });

    const activeIndex = this.instance.activeIndex;
    const visibleIndexes = window.innerWidth >= 1024 && this.instance.el.classList.contains('is-split-state')
      ? [activeIndex, activeIndex + 1]
      : [activeIndex];

    visibleIndexes.forEach(index => {
      const target = thumbSlides.find(slide => Number(slide.dataset.index) === index);
      if (target) target.classList.add('is-visible-pair');
    });
  }

  initGalleryPreviews() {
    const strip = this.querySelector('[data-gallery-previews]');
    if (!strip || !this.instance?.el) return;

    // Move inside .swiper as sibling of .swiper-buttons
    this.instance.el.appendChild(strip);

    // Click to navigate
    strip.querySelectorAll('[data-preview-index]').forEach(item => {
      item.addEventListener('click', () => {
        const idx = parseInt(item.dataset.previewIndex, 10);
        if (this.isCustomLayout && typeof this._applyDesktopState === 'function') {
          this._applyDesktopState(idx);
        } else {
          this.instance.slideTo(idx);
        }
      });
    });

    this.updateGalleryPreviews();
    this.instance.on('slideChange', () => this.updateGalleryPreviews());
    this.instance.on('update', () => this.updateGalleryPreviews());
    this.instance.on('transitionEnd', () => this.updateGalleryPreviews());
  }

  updateGalleryPreviews() {
    const strip = this.instance?.el?.querySelector('[data-gallery-previews]');
    if (!strip) return;

    const activeIndex = this.instance.activeIndex;
    const spv = parseFloat(this.instance.params.slidesPerView) || 1;
    const isSplit = spv >= 2;
    const visibleIndexes = isSplit ? [activeIndex, activeIndex + 1] : [activeIndex];

    strip.querySelectorAll('[data-preview-index]').forEach(item => {
      const idx = parseInt(item.dataset.previewIndex, 10);
      item.classList.toggle('is-active', visibleIndexes.includes(idx));
    });
  }

  photoSwipeLightboxInit() {
    const [closeIcon, prevIcon, nextIcon] = [
      document.querySelector('[data-close-icon]'),
      document.querySelector('[data-prev-icon]'),
      document.querySelector('[data-next-icon]')
    ];

    const photoSwipeLightboxInstance = new PhotoSwipeLightbox({
      gallery: `.photoswipe-wrapper`,
      children: 'a.product__gallery-toggle',
      mainClass: 'pswp--product-media-gallery',

      loop: false,
      showHideAnimationType: 'zoom',

      initialZoomLevel: zoomLevelObject => {
        if (DeviceDetector.isMobile()) {
          return zoomLevelObject.vFill;
        } else {
          return zoomLevelObject.fit;
        }
      },
      secondaryZoomLevel: zoomLevelObject => {
        if (DeviceDetector.isMobile()) {
          return zoomLevelObject.fit;
        } else {
          return 1;
        }
      },
      pswpModule: PhotoSwipe
    });
    photoSwipeLightboxInstance.addFilter('uiElement', (element, data) => {
      if (data.name === 'close') {
        element.innerHTML = closeIcon.innerHTML;
      } else if (data.name === 'arrowPrev') {
        element.innerHTML = prevIcon.innerHTML;
      } else if (data.name === 'arrowNext') {
        element.innerHTML = nextIcon.innerHTML;
      }
      return element;
    });

    // html for video
    photoSwipeLightboxInstance.addFilter('itemData', (itemData, index) => {
      if (itemData.type === 'html' && itemData.element) {
        return {
          html: itemData.element.dataset.pswpHtml
        };
      }
      return itemData;
    });

    photoSwipeLightboxInstance.init();

    photoSwipeLightboxInstance.on('beforeOpen', () => {
      document.body.classList.add('overflow-hidden');
      const videos = this.querySelectorAll('video');
      Array.from(videos).forEach(video => {
        // If video is not playing, call playPromise
        // Force to play
        video
          .play()
          .then(() => {
            // Automatic playback started!
            // Show playing UI.
          })
          .catch(error => {
            // Auto-play was prevented
            // Show paused UI.
            video.pause();
          });
      });
    });

    photoSwipeLightboxInstance.on('closingAnimationStart', () => {
      document.body.classList.remove('overflow-hidden');
    });
  }

  setActiveMedia(id) {
    const mediaFound = Array.from(this.querySelectorAll('[data-media-id]')).find(media => Number(media.dataset.mediaId) === id);

    if (!mediaFound) return;

    if (this.instance && typeof this.instance.slideTo === 'function') {
      if (this.dataset.hideOtherVariantsMedia === 'false') {
        this.instance.slideTo(Number(mediaFound.dataset.index));
      }
    }
  }

  disconnectedCallback() {
    if (this._splitTransitionTimer) {
      clearTimeout(this._splitTransitionTimer);
      this._splitTransitionTimer = null;
    }
    if (this._customInitTimer) {
      clearTimeout(this._customInitTimer);
      this._customInitTimer = null;
    }
    if (this._customBoundResizeHandler) {
      window.removeEventListener('resize', this._customBoundResizeHandler);
      this._customBoundResizeHandler = null;
    }
    this._customInteractionBound = false;
    super.disconnectedCallback();
  }
}
if (!customElements.get('swiper-product-gallery')) {
  customElements.define('swiper-product-gallery', InstanceSwiperProductGallery);
}

/**
 * Product Media Info Class
 */
class ProductMediaInfo extends HTMLElement {
  constructor() {
    super();

    this.init();
    window.addEventListener('resize', this.init.bind(this));

    if (Shopify.designMode) {
      window.addEventListener('shopify:section:load', this.init.bind(this));
    }
  }

  init() {
    let containerOffsetWidth = document.querySelector('.main-product__media--slider')?.offsetWidth || 330;
    if (document.querySelector('.main-product__media--grid-item') && !DeviceDetector.isMobile()) {
      const gridItem = document.querySelector('.main-product__media--grid-item');
      if (gridItem) containerOffsetWidth = gridItem.offsetWidth;
    }
    const maxWidthForInfo = (containerOffsetWidth - 48) / 2;

    const lastP = this.querySelector('p:last-child');
    if (!lastP) return;

    const mediaInfoTextWidth = lastP.offsetWidth;
    const mediaInfoHidden = this.querySelector('p[aria-hidden]');

    if (mediaInfoTextWidth > maxWidthForInfo) {
      this.classList.remove('animation-stopped');
      this.style.cssText = `--marquee-speed: ${(mediaInfoTextWidth / maxWidthForInfo) * 8}s`;
      if (mediaInfoHidden) mediaInfoHidden.style.display = '';
    } else {
      this.classList.add('animation-stopped');
      this.style.cssText = '';
      if (mediaInfoHidden) mediaInfoHidden.style.display = 'none';
    }
  }
}

if (!customElements.get('product-media-info')) {
  customElements.define('product-media-info', ProductMediaInfo);
}

function initCustomPdpGalleryFallback() {
  document.querySelectorAll('.product--custom-layout swiper-product-gallery[handle="product-gallery"]').forEach(el => {
    if (typeof el.setCustomLayoutInteraction !== 'function') return;
    if (!el.instance || el._customInteractionBound) return;
    el.isCustomLayout = true;
    el.setCustomLayoutInteraction();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(initCustomPdpGalleryFallback, 120);
  });
} else {
  setTimeout(initCustomPdpGalleryFallback, 120);
}

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    setTimeout(initCustomPdpGalleryFallback, 120);
  });
}

/**
 * DesktopProductGallery — CSS carousel estilo Gucci para ≥1024px.
 *
 * Efecto hero→split:
 *   active=0 → slide[0] ocupa el 100% (is-hero), track en tx=0.
 *   active=1 → slide[0] al 50%, track en tx=0.
 *              El encogimiento de slide[0] revela slide[1] desde la derecha
 *              sin que el track se mueva — este es el efecto Gucci.
 *   active=N (N≥2) → tx = -(N-1) × 50% del contenedor.
 *              Deslizamiento normal entre pares de slides.
 *
 * Mobile/tablet (<1024px) sigue usando el Swiper existente — sin cambios.
 */
class DesktopProductGallery {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('[data-gallery-desktop-track]');
    this.slides = Array.from(el.querySelectorAll('[data-gallery-desktop-slide]'));
    this.total = this.slides.length;
    this.active = 0;
    this.btnPrev = el.querySelector('[data-gallery-desktop-prev]');
    this.btnNext = el.querySelector('[data-gallery-desktop-next]');
    this.currentEl = el.querySelector('[data-gallery-desktop-current]');
    this.previews = el.querySelector('[data-desktop-gallery-previews]');
    this.hitbox = el.querySelector('.product-gallery-desktop__controls-hitbox');

    if (!this.track || !this.slides.length) return;

    this._dragStartX = 0;
    this._dragActive = false;
    this._isDragging = false;

    // Sin imagen hero del metacampo: nunca mostrar hero suelto, siempre pares fijos de dos.
    this.hasHeroImage = el.dataset.hasHeroImage === 'true';
    this._applyState(this.hasHeroImage ? 0 : 1, false);
    this._bindEvents();
    this._updatePreviewPosition();
    this._updateControlsHitbox();
  }

  /** Ajusta el hitbox al área real de miniaturas + botones, sin importar cuántas haya. */
  _updateControlsHitbox() {
    if (!this.hitbox) return;
    const nodes = [this.previews, this.btnPrev, this.btnNext].filter(Boolean);
    const rects = nodes.map(node => node.getBoundingClientRect()).filter(rect => rect.width > 0 && rect.height > 0);
    if (!rects.length) return;

    const galleryRect = this.el.getBoundingClientRect();
    const padding = 12;
    const left = Math.max(Math.min(...rects.map(r => r.left)) - galleryRect.left - padding, 0);
    const top = Math.max(Math.min(...rects.map(r => r.top)) - galleryRect.top - padding, 0);
    const right = Math.max(...rects.map(r => r.right)) - galleryRect.left + padding;
    const bottom = Math.max(...rects.map(r => r.bottom)) - galleryRect.top + padding;

    this.hitbox.style.left = `${left}px`;
    this.hitbox.style.top = `${top}px`;
    this.hitbox.style.width = `${right - left}px`;
    this.hitbox.style.height = `${bottom - top}px`;
  }

  /** Alinea un índice solicitado (p. ej. clic en miniatura) evitando el hero suelto sin imagen del metacampo. */
  _resolveTargetIndex(requestedIndex) {
    const min = this.hasHeroImage ? 0 : 1;
    return Math.min(Math.max(requestedIndex, min), this.total - 1);
  }

  _bindEvents() {
    // Botones de flecha
    this.btnPrev?.addEventListener('click', e => {
      e.stopPropagation();
      this._goPrev();
    });
    this.btnNext?.addEventListener('click', e => {
      e.stopPropagation();
      this._goNext();
    });

    // Navegación por clic eliminada: se navega con botones, drag y preview strip.
    // El clic en cualquier slide abre el zoom (gestionado por gallery-fullscreen.js).

    // Drag con ratón
    this.el.addEventListener('mousedown', e => {
      this._dragStartX = e.clientX;
      this._dragActive = true;
      this._isDragging = false;
    });
    this.el.addEventListener('mousemove', e => {
      if (!this._dragActive) return;
      if (Math.abs(e.clientX - this._dragStartX) > 8) {
        this._isDragging = true;
      }
    });
    this.el.addEventListener('mouseup', e => {
      if (!this._dragActive) return;
      this._dragActive = false;
      const diff = e.clientX - this._dragStartX;
      if (Math.abs(diff) > 60) {
        if (diff < 0) this._goNext();
        else this._goPrev();
      }
      // Pequeño delay para que el click handler no se dispare tras el drag
      setTimeout(() => { this._isDragging = false; }, 50);
    });
    this.el.addEventListener('mouseleave', () => {
      this._dragActive = false;
    });

    // Touch / swipe
    this.el.addEventListener('touchstart', e => {
      this._dragStartX = e.touches[0].clientX;
    }, { passive: true });
    this.el.addEventListener('touchend', e => {
      const diff = e.changedTouches[0].clientX - this._dragStartX;
      if (Math.abs(diff) > 60) {
        if (diff < 0) this._goNext();
        else this._goPrev();
      }
    }, { passive: true });

    // Click en preview strip desktop
    const previews = this.el.querySelector('[data-desktop-gallery-previews]');
    if (previews) {
      previews.addEventListener('click', e => {
        const item = e.target.closest('[data-desktop-preview-index]');
        if (!item) return;
        this._applyState(this._resolveTargetIndex(Number(item.dataset.desktopPreviewIndex)));
      });
    }

    // Clicks en thumbnails del Swiper (sincronizar con galería desktop)
    const thumbsWrapper = this.el.closest('.main-product__media')
      ?.querySelector('.main-product__media--thumbs-wrapper');
    if (thumbsWrapper) {
      thumbsWrapper.addEventListener('click', e => {
        const thumb = e.target.closest('.swiper-slide[data-index]');
        if (!thumb) return;
        this._applyState(this._resolveTargetIndex(Number(thumb.dataset.index)));
      });
    }

    // Resize: recalcular posición sin animación
    this._onResize = () => {
      this._applyState(this.active, false);
      this._updatePreviewPosition();
      this._updateControlsHitbox();
    };
    window.addEventListener('resize', this._onResize);

    this._onScroll = () => {
      this._updatePreviewPosition();
      this._updateControlsHitbox();
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  /**
   * Aplica el estado de la galería para un índice dado.
   * @param {number} index  - Índice del slide a activar.
   * @param {boolean} animate - Si false, suprime las CSS transitions.
   */
  _applyState(index, animate = true) {
    const clamped = Math.max(0, Math.min(index, this.total - 1));
    const w = this.el.offsetWidth;
    const isHero = this.hasHeroImage && clamped === 0;

    if (!animate) {
      this.track.style.transition = 'none';
    }

    // Slide 0: toggle clase is-hero (CSS hace el transition de width)
    this.slides[0]?.classList.toggle('is-hero', isHero);

    // Cálculo de la traslación del track:
    //   active=0 → tx=0  (hero ocupa 100%, splits fuera de pantalla)
    //   active=1 → tx=0  (sin movimiento, el encogimiento de slide[0] revela slide[1])
    //   active=N (N≥2) → tx = -(N-1) × 0.5 × containerWidth
    const tx = isHero ? 0 : -((clamped - 1) * w * 0.5);
    this.track.style.transform = `translateX(${tx}px)`;

    // Restaurar transitions en el siguiente frame
    if (!animate) {
      requestAnimationFrame(() => {
        this.track.style.transition = '';
      });
    }

    this.active = clamped;
    this._updateUI();
  }

  _goNext() {
    if (this.active >= this.total - 1) {
      this._applyState(this.hasHeroImage ? 0 : 1);
    } else {
      this._applyState(this.active + 1);
    }
  }

  _goPrev() {
    const min = this.hasHeroImage ? 0 : 1;
    if (this.active <= min) {
      this._applyState(this.total - 1);
    } else {
      this._applyState(this.active - 1);
    }
  }

  _updateUI() {
    // Estado de botones (sin is-disabled ya que hay loop)
    this.btnPrev?.classList.toggle('is-disabled', false);
    this.btnNext?.classList.toggle('is-disabled', false);

    // Ambos slides visibles reciben zoom on click
    this.slides.forEach(slide => {
      slide.classList.remove('is-clickable-next');
      slide.classList.remove('is-zoom-target');
    });
    if (this.active === 0) {
      this.slides[0]?.classList.add('is-zoom-target');
    } else {
      // Split: izquierdo y derecho hacen zoom
      this.slides[this.active - 1]?.classList.add('is-zoom-target');
      this.slides[this.active]?.classList.add('is-zoom-target');
    }

    // Preview strip desktop: marcar item activo
    const previews = this.el.querySelector('[data-desktop-gallery-previews]');
    if (previews) {
      previews.querySelectorAll('[data-desktop-preview-index]').forEach(item => {
        item.classList.toggle('is-active', Number(item.dataset.desktopPreviewIndex) === this.active);
      });
    }

    // Sincronizar clase activa en los thumbs del Swiper
    const thumbsWrapper = this.el.closest('.main-product__media')
      ?.querySelector('.main-product__media--thumbs-wrapper');
    if (thumbsWrapper) {
      thumbsWrapper.querySelectorAll('.swiper-slide[data-index]').forEach(slide => {
        const idx = Number(slide.dataset.index);
        slide.classList.toggle('swiper-slide-thumb-active', idx === this.active);
      });
    }
  }

  _updatePreviewPosition() {
    const galleryRect = this.el.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const previewHeight = this.previews?.offsetHeight || 0;
    const shouldStick = galleryRect.top <= viewportHeight - 32
      && galleryRect.bottom > previewHeight + 32;

    this.previews?.classList.toggle('is-viewport-sticky', shouldStick);

    [this.btnPrev, this.btnNext].forEach(button => {
      button?.classList.toggle('is-viewport-sticky', shouldStick);
    });

    if (shouldStick) {
      const bottom = Math.max(32, viewportHeight - galleryRect.bottom + 32);
      this.previews?.style.setProperty('--gallery-previews-bottom', `${bottom}px`);

      const controlsBottom = Math.max(48, viewportHeight - galleryRect.bottom + 48);
      [this.btnPrev, this.btnNext].forEach(button => {
        button?.style.setProperty('--gallery-controls-bottom', `${controlsBottom}px`);
      });
    } else {
      this.previews?.style.removeProperty('--gallery-previews-bottom');
      [this.btnPrev, this.btnNext].forEach(button => {
        button?.style.removeProperty('--gallery-controls-bottom');
      });
    }
  }

  destroy() {
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('scroll', this._onScroll);
  }
}

// Inicialización
function initDesktopProductGalleries() {
  document.querySelectorAll('.product--custom-layout [data-gallery-desktop]').forEach(el => {
    if (el._desktopGallery) return; // evitar doble init
    el._desktopGallery = new DesktopProductGallery(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDesktopProductGalleries);
} else {
  initDesktopProductGalleries();
}

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => setTimeout(initDesktopProductGalleries, 100));
}
